import React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import './LoginModal.css';

import {
    RecaptchaVerifier,
    signInWithPhoneNumber,
    GoogleAuthProvider,
    signInWithPopup,
    updateProfile
} from 'firebase/auth';
import { auth } from '../../firebase';
import { syncUserToFirestore } from '../../services/ProfileService';
import { useLocation } from '../../contexts/LocationContext';

// ─── Constants ────────────────────────────────────────────────────────────────
const STEPS = {
    TRIGGER: 'trigger',
    PHONE: 'phone',
    OTP: 'otp',
    SUCCESS: 'success',
};
const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

// ─── Utility: lock body scroll while modal is open ────────────────────────────
function useLockBodyScroll(active) {
    useEffect(() => {
        if (!active) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [active]);
}

// ─── OTP input sub-component ──────────────────────────────────────────────────
function OtpInput({ value, onChange }) {
    const inputs = useRef([]);

    const handleKey = (e, idx) => {
        if (e.key === 'Backspace') {
            if (!value[idx] && idx > 0) inputs.current[idx - 1]?.focus();
            const next = value.split('');
            next[idx] = '';
            onChange(next.join(''));
            return;
        }
        if (e.key === 'ArrowLeft' && idx > 0) {
            inputs.current[idx - 1]?.focus();
            return;
        }
        if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) {
            inputs.current[idx + 1]?.focus();
            return;
        }
    };

    const handleChange = (e, idx) => {
        const raw = e.target.value.replace(/\D/g, '');
        if (!raw) return;
        const digit = raw[raw.length - 1];
        const next = value.split('').slice(0, OTP_LENGTH);
        while (next.length < OTP_LENGTH) next.push('');
        next[idx] = digit;
        onChange(next.join(''));
        if (idx < OTP_LENGTH - 1) inputs.current[idx + 1]?.focus();
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData
            .getData('text')
            .replace(/\D/g, '')
            .slice(0, OTP_LENGTH);
        onChange(pasted.padEnd(OTP_LENGTH, ''));
        const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
        inputs.current[focusIdx]?.focus();
    };

    return (
        <div className="lm-otp-row">
            {Array.from({ length: OTP_LENGTH }).map((_, idx) => (
                <input
                    key={idx}
                    ref={(el) => (inputs.current[idx] = el)}
                    className={`lm-otp-box${value[idx] ? ' filled' : ''}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={value[idx] || ''}
                    onChange={(e) => handleChange(e, idx)}
                    onKeyDown={(e) => handleKey(e, idx)}
                    onPaste={handlePaste}
                    aria-label={`OTP digit ${idx + 1}`}
                    autoComplete="one-time-code"
                />
            ))}
        </div>
    );
}

// ─── Resend countdown ─────────────────────────────────────────────────────────
function ResendTimer({ onResend }) {
    const [secs, setSecs] = useState(RESEND_SECONDS);

    useEffect(() => {
        if (secs <= 0) return;
        const t = setTimeout(() => setSecs((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [secs]);

    const handleResend = () => {
        setSecs(RESEND_SECONDS);
        onResend();
    };

    return (
        <p className="lm-resend">
            {secs > 0 ? (
                <>
                    Didn't receive it? Resend in{' '}
                    <span className="lm-resend-timer">
                        0:{String(secs).padStart(2, '0')}
                    </span>
                </>
            ) : (
                <>
                    Didn't receive it?{' '}
                    <button className="lm-resend-btn" onClick={handleResend}>
                        Resend OTP
                    </button>
                </>
            )}
        </p>
    );
}

// ─── Google icon ──────────────────────────────────────────────────────────────
function GoogleIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
        >
            <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
            />
            <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
            />
            <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                fill="#FBBC05"
            />
            <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
            />
        </svg>
    );
}

// ─── Step icons ───────────────────────────────────────────────────────────────
function CheckIcon() {
    return (
        <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M20 6L9 17l-5-5" />
        </svg>
    );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
/**
 * Props:
 *   isOpen        {boolean}  — controls visibility
 *   onClose       {fn}       — called when user dismisses
 *   onSuccess     {fn(user)} — called after successful login/signup
 *   trigger       {string}   — "chat" | "post" | "generic"  (what triggered the modal)
 *   listingContext {object}  — optional { title, price, location } for contextual display
 */
export default function LoginModal({
    isOpen = false,
    onClose,
    onSuccess,
    trigger = 'generic',
    listingContext = null,
}) {

    const { location: currentSessionLocation } = useLocation();

    const [renderModal, setRenderModal] = useState(false);
    const [showAnimation, setShowAnimation] = useState(false);

    const [step, setStep] = useState(STEPS.TRIGGER);
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [otp, setOtp] = useState('');
    const [otpError, setOtpError] = useState('');
    const [loading, setLoading] = useState(false);
    // 2. NEW STATE: Store Firebase's confirmation result and User
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [loggedInUser, setLoggedInUser] = useState(null);
    const overlayRef = useRef(null);
    const phoneInputRef = useRef(null);
    const recaptchaVerifierRef = useRef(null);
    const recaptchaWidgetIdRef = useRef(null);



    useLockBodyScroll(isOpen);

    // 2. NEW ANIMATION EFFECT
    useEffect(() => {
        let raf1, raf2, timeoutId;

        if (isOpen) {
            setRenderModal(true);

            // ✅ Ensure DOM is painted before animation starts
            raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => {
                    setShowAnimation(true);
                });
            });

        } else {
            setShowAnimation(false);

            timeoutId = setTimeout(() => {
                setRenderModal(false);
            }, 400);
        }

        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            clearTimeout(timeoutId);
        };
    }, [isOpen]);

    // Reset state whenever modal opens
    useEffect(() => {
        if (isOpen) {
            const initial = listingContext ? STEPS.TRIGGER : STEPS.PHONE;
            setStep(initial);
            setPhone('');
            setOtp('');
            setPhoneError('');
            setOtpError('');
            setLoading(false);
            setConfirmationResult(null);
            setLoggedInUser(null);
        }
    }, [isOpen, listingContext]);

    useEffect(() => {
        return () => {
            if (recaptchaVerifierRef.current) {
                recaptchaVerifierRef.current.clear();
                recaptchaVerifierRef.current = null;
            }
            recaptchaWidgetIdRef.current = null;
            window.recaptchaVerifier = null;
        };
    }, []);

    // Auto-focus phone input when entering PHONE step
    useEffect(() => {
        if (step === STEPS.PHONE) {
            setTimeout(() => phoneInputRef.current?.focus(), 80);
        }
    }, [step]);

    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) onClose?.();
    };

    const handleEscape = useCallback(
        (e) => {
            if (e.key === 'Escape') onClose?.();
        },
        [onClose]
    );

    useEffect(() => {
        if (!isOpen) return;
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, handleEscape]);

    const validatePhone = (val) => {
        const digits = val.replace(/\D/g, '');
        if (!digits) return 'Please enter your mobile number.';
        if (digits.length !== 10) return 'Enter a valid 10-digit mobile number.';
        if (!/^[6-9]/.test(digits))
            return 'Mobile number must start with 6, 7, 8, or 9.';
        return '';
    };

    const resetRecaptcha = useCallback(async () => {
        if (recaptchaWidgetIdRef.current !== null && window.grecaptcha) {
            window.grecaptcha.reset(recaptchaWidgetIdRef.current);
        }

        if (recaptchaVerifierRef.current) {
            recaptchaVerifierRef.current.clear();
            recaptchaVerifierRef.current = null;
        }

        recaptchaWidgetIdRef.current = null;
        window.recaptchaVerifier = null;
    }, []);

    // 3. NEW: Setup Firebase Recaptcha
    const setupRecaptcha = useCallback(async () => {
        const container = document.getElementById('recaptcha-container');
        console.log("recaptcha exists?", !!container);
        if (!container) {
            throw new Error('reCAPTCHA container not found.');
        }

        // 🔥 ALWAYS CLEAR OLD INSTANCE
        if (recaptchaVerifierRef.current) {
            try {
                recaptchaVerifierRef.current.clear();
            } catch (e) {
                console.warn("Error clearing verifier:", e);
            }
            recaptchaVerifierRef.current = null;
        }

        const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
            callback: () => { },
            'expired-callback': () => {
                setPhoneError("reCAPTCHA expired. Please try again.");
            }
        });

        recaptchaVerifierRef.current = verifier;
        window.recaptchaVerifier = verifier;

        await verifier.render();

        return verifier;
    }, []);
    // 4. FIREBASE: Send OTP
    const handleSendOtp = async () => {
        const err = validatePhone(phone);
        if (err) {
            setPhoneError(err);
            return;
        }
        setPhoneError('');
        setLoading(true);

        try {
            auth.languageCode = 'en';
            const appVerifier = await setupRecaptcha();
            const formattedPhone = `+91${phone.replace(/\D/g, "")}`; // Add India country code

            const confirmation = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
            setConfirmationResult(confirmation); // Save this to verify later
            setStep(STEPS.OTP);
        } catch (error) {
            console.error("Error sending OTP:", error);
            setPhoneError("Failed to send OTP. " + error.message);
            await resetRecaptcha();
        } finally {
            setLoading(false);
        }
    };

    const provideAndUpdateUserPhotoUrl = async (user) => {
        // 1. If the top-level photoURL already exists, we are done.
        if (user.photoURL) return;

        // 2. Check providerData (Google usually fills this automatically, but safe to check)
        if (user.providerData && user.providerData.length > 0) {
            const provider = user.providerData[0];
            if (provider.photoURL) return;
        }

        // 3. Generate the Fallback Name (Matches your mobile logic)
        const lastFour = user.phoneNumber ? user.phoneNumber.slice(-4) : Math.floor(1000 + Math.random() * 9000);
        const tempName = user.displayName || `User${lastFour}`;

        // 4. Generate the Avatar URL
        // I updated this to use your Qinto Burgundy color (#8E24AA) 
        // and removed 'random' to keep the branding consistent.
        const photoUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(tempName)}&background=8E24AA&color=fff&rounded=true&size=128`;

        try {
            // Use the standalone updateProfile function
            await updateProfile(user, {
                displayName: tempName,
                photoURL: photoUrl
            });
            console.log("Auth Profile auto-initialized successfully");
        } catch (error) {
            console.error("Error updating Auth profile:", error);
        }
    }

    // 5. FIREBASE: Verify OTP
    const handleVerifyOtp = async () => {
        if (otp.replace(/\D/g, '').length < OTP_LENGTH) {
            setOtpError('Please enter the complete 6-digit OTP.');
            return;
        }
        setOtpError('');
        setLoading(true);

        try {
            const credential = await confirmationResult.confirm(otp);
            await provideAndUpdateUserPhotoUrl(credential.user); // Ensure user has a photoURL
            await syncUserToFirestore(credential.user, 'phone', currentSessionLocation); // Sync to Firestore with signup method
            setLoggedInUser(credential.user);
            setStep(STEPS.SUCCESS);
        } catch (error) {
            console.error("Error verifying OTP:", error);
            setOtpError("Invalid OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // 6. FIREBASE: Google Sign-In
    const handleGoogleSignIn = async () => {
        setLoading(true);
        try {
            const provider = new GoogleAuthProvider();
            const credential = await signInWithPopup(auth, provider);
            await provideAndUpdateUserPhotoUrl(credential.user); // Ensure user has a photoURL
            await syncUserToFirestore(credential.user, 'google', currentSessionLocation); // Sync to Firestore with signup method
            setLoggedInUser(credential.user);
            setStep(STEPS.SUCCESS);
        } catch (error) {
            console.error("Google Sign-In Error:", error);
            setPhoneError("Google Sign-In failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // 7. FIREBASE: Resend OTP
    const handleResend = async () => {
        setOtpError('');
        try {
            const appVerifier = await setupRecaptcha();
            const formattedPhone = `+91${phone.replace(/\D/g, "")}`;

            const confirmation = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
            setConfirmationResult(confirmation);
            console.log('OTP Resent');
        } catch (error) {
            console.error("Error resending OTP:", error);
            setOtpError("Failed to resend OTP. Please try again.");
            await resetRecaptcha();
        }
    };

    const handleSuccess = () => {
        onSuccess?.(loggedInUser); // Pass the actual Firebase User object up
        onClose?.();
    };

    const triggerCopy = {
        chat: {
            badge: 'Secure Chat',
            title: 'Sign in to chat safely',
            hint: 'To protect our community from spam, you need a verified account to message sellers.'
        },
        post: {
            badge: 'Free Ad',
            title: 'Sign in to start selling',
            hint: 'Create a free account to post your ad and manage your listings.'
        },
        generic: {
            badge: 'Welcome',
            title: 'Welcome to Qinto',
            hint: 'Create a free account to buy, sell, and chat securely.'
        },
    }[trigger] || { badge: null, title: 'Sign in to continue', hint: null };

    if (!renderModal) return null;

    return (
        <div
            className={`lm-overlay ${showAnimation ? 'open' : ''}`}
            ref={overlayRef}
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-label="Sign in to Qinto"
        >
            <div className="lm-modal">
                {/* 8. CRITICAL: INVISIBLE RECAPTCHA CONTAINER */}
                {/* <div id="recaptcha-container"></div> */}
                {/* Close button */}
                <button className="lm-close" onClick={onClose} aria-label="Close">
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                    >
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>

                {/* ── STEP: Trigger ── */}
                {step === STEPS.TRIGGER && (
                    <div className="lm-step">
                        <div className="lm-logo">
                            <img
                                src="/logos/Qinto.png"
                                alt="Qinto Logo"
                                className="logo-image"
                            />
                        </div>
                        {triggerCopy.badge && (
                            <div className="lm-trigger-badge">{triggerCopy.badge}</div>
                        )}

                        {listingContext && (
                            <div className="lm-listing-card">
                                <div className="lm-listing-thumb" aria-hidden="true">
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    >
                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                        <path d="M3 9h18" />
                                        <circle cx="8" cy="6" r="1" fill="currentColor" />
                                    </svg>
                                </div>
                                <div className="lm-listing-info">
                                    <div className="lm-listing-title">{listingContext.title}</div>
                                    <div className="lm-listing-meta">
                                        {listingContext.price} · {listingContext.location}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* <div className="lm-trigger-box">
                            <p className="lm-trigger-title">Sign in to continue</p>
                            <p className="lm-trigger-desc">
                                {triggerCopy.hint ||
                                    'Create a free account to buy, sell, and chat on Qinto.'}{' '}
                                Your phone number is never shared with anyone.
                            </p>
                        </div> */}

                        <div className="lm-trigger-box">
                            {/* Use the dynamic title based on what they clicked */}
                            <p className="lm-trigger-title">{triggerCopy.title}</p>

                            <p className="lm-trigger-desc">
                                {triggerCopy.hint}
                                <br /><br />
                                {/* Always keep this trust-builder visible! */}
                                <strong>🔒 Your phone number is strictly private and never shared publicly.</strong>
                            </p>
                        </div>

                        <button
                            className="lm-btn-primary"
                            onClick={() => setStep(STEPS.PHONE)}
                        >
                            Continue with mobile number →
                        </button>
                        <button
                            className="lm-btn-google"
                            onClick={handleGoogleSignIn}
                            disabled={loading}
                        >
                            <GoogleIcon /> Continue with Google
                        </button>
                        <p className="lm-terms">
                            By continuing you agree to our{' '}
                            <a href="/terms" target="_blank" rel="noreferrer">
                                Terms
                            </a>{' '}
                            &amp;{' '}
                            <a href="/privacy" target="_blank" rel="noreferrer">
                                Privacy Policy
                            </a>
                        </p>
                    </div>
                )}

                {/* ── STEP: Phone entry ── */}
                {step === STEPS.PHONE && (
                    <div className="lm-step">
                        <div className="lm-logo">
                            <img
                                src="/logos/Qinto.png"
                                alt="Qinto Logo"
                                className="logo-image"
                            />
                        </div>
                        <h2 className="lm-title">Enter your mobile number</h2>
                        <p className="lm-subtitle">
                            We'll send a 6-digit OTP. No password needed.
                        </p>

                        <label className="lm-label" htmlFor="lm-phone">
                            Mobile number
                        </label>
                        <div className="lm-phone-row">
                            <div className="lm-cc">+91</div>
                            <input
                                id="lm-phone"
                                ref={phoneInputRef}
                                className={`lm-input lm-input-phone${phoneError ? ' error' : ''
                                    }`}
                                type="tel"
                                inputMode="numeric"
                                placeholder="98765 43210"
                                maxLength={11}
                                value={phone}
                                onChange={(e) => {
                                    setPhone(e.target.value.replace(/[^\d\s]/g, ''));
                                    if (phoneError) setPhoneError('');
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSendOtp();
                                }}
                                autoComplete="tel-national"
                            />
                        </div>
                        {phoneError && <p className="lm-error">{phoneError}</p>}

                        <button
                            className="lm-btn-primary"
                            onClick={handleSendOtp}
                            disabled={loading}
                        >
                            {loading ? <span className="lm-spinner" /> : 'Send OTP'}
                        </button>

                        <div className="lm-divider">
                            <span>or</span>
                        </div>

                        <button
                            className="lm-btn-google"
                            onClick={handleGoogleSignIn}
                            disabled={loading}
                        >
                            <GoogleIcon /> Continue with Google
                        </button>

                        <p className="lm-terms">We never share your number publicly.</p>

                        {listingContext && (
                            <button
                                className="lm-back"
                                onClick={() => setStep(STEPS.TRIGGER)}
                            >
                                ← Back
                            </button>
                        )}
                    </div>
                )}

                {/* ── STEP: OTP verification ── */}
                {step === STEPS.OTP && (
                    <div className="lm-step">
                        <div className="lm-logo">
                            <img
                                src="/logos/Qinto.png"
                                alt="Qinto Logo"
                                className="logo-image"
                            />
                        </div>
                        <h2 className="lm-title">Enter the OTP</h2>
                        <p className="lm-subtitle">Sent to your number</p>

                        <div className="lm-phone-preview">
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                aria-hidden="true"
                            >
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.82a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                            <span>+91 {phone}</span>
                            <button
                                className="lm-change"
                                onClick={() => setStep(STEPS.PHONE)}
                            >
                                Change
                            </button>
                        </div>

                        <OtpInput
                            value={otp}
                            onChange={(v) => {
                                setOtp(v);
                                if (otpError) setOtpError('');
                            }}
                        />
                        {otpError && <p className="lm-error lm-error-center">{otpError}</p>}

                        <ResendTimer onResend={handleResend} />

                        <button
                            className="lm-btn-primary"
                            onClick={handleVerifyOtp}
                            disabled={loading || otp.replace(/\D/g, '').length < OTP_LENGTH}
                        >
                            {loading ? (
                                <span className="lm-spinner" />
                            ) : (
                                'Verify & continue →'
                            )}
                        </button>

                        <p className="lm-terms">OTP expires in 10 minutes</p>
                    </div>
                )}

                {/* ── STEP: Success ── */}
                {step === STEPS.SUCCESS && (
                    <div className="lm-step lm-step-success">
                        <div className="lm-success-icon" aria-hidden="true">
                            <CheckIcon />
                        </div>
                        <h2 className="lm-title">You're signed in!</h2>
                        <p className="lm-subtitle">
                            {phone ? `+91 ${phone}` : 'Signed in with Google'}
                        </p>

                        <div className="lm-success-actions">
                            {trigger === 'chat' && (
                                <button
                                    className="lm-action-card lm-action-primary"
                                    onClick={handleSuccess}
                                >
                                    <div className="lm-action-icon lm-action-icon-blue">
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                        </svg>
                                    </div>
                                    <div className="lm-action-text">
                                        <div className="lm-action-label">Chat with seller</div>
                                        <div className="lm-action-sub">
                                            Continue where you left off
                                        </div>
                                    </div>
                                    <span className="lm-action-arrow" aria-hidden="true">
                                        ›
                                    </span>
                                </button>
                            )}
                            {trigger === 'post' && (
                                <button
                                    className="lm-action-card lm-action-primary"
                                    onClick={handleSuccess}
                                >
                                    <div className="lm-action-icon lm-action-icon-teal">
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                        >
                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                            <path d="M12 8v8M8 12h8" />
                                        </svg>
                                    </div>
                                    <div className="lm-action-text">
                                        <div className="lm-action-label">Post your ad</div>
                                        <div className="lm-action-sub">Start selling today</div>
                                    </div>
                                    <span className="lm-action-arrow" aria-hidden="true">
                                        ›
                                    </span>
                                </button>
                            )}
                            {trigger === 'chat' && (
                                <button
                                    className="lm-action-card"
                                    onClick={() => {
                                        setStep(STEPS.SUCCESS);
                                        onClose?.();
                                    }}
                                >
                                    <div className="lm-action-icon lm-action-icon-teal">
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                        >
                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                            <path d="M12 8v8M8 12h8" />
                                        </svg>
                                    </div>
                                    <div className="lm-action-text">
                                        <div className="lm-action-label">Post a free ad</div>
                                        <div className="lm-action-sub">Sell something today</div>
                                    </div>
                                    <span className="lm-action-arrow" aria-hidden="true">
                                        ›
                                    </span>
                                </button>
                            )}
                            {trigger === 'generic' && (
                                <button
                                    className="lm-action-card lm-action-primary"
                                    onClick={handleSuccess}
                                >
                                    <div className="lm-action-icon lm-action-icon-blue">
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                        >
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M12 8v4l3 3" />
                                        </svg>
                                    </div>
                                    <div className="lm-action-text">
                                        <div className="lm-action-label">Go to home</div>
                                        <div className="lm-action-sub">Start browsing listings</div>
                                    </div>
                                    <span className="lm-action-arrow" aria-hidden="true">
                                        ›
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
